import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { DEFAULT_COMPRESS_PDF_SETTINGS, compressPdfFile, formatFileSize } from './compressPdfEngine';
import { applyPdfPageEdits, pagesHaveEdits } from '../shared/pdfPageEditEngine';
import { zipPdfFiles } from '../split/splitPdfEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfFileRow } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

/** On/off row: label and hint on the left, switch on the right. */
function SettingToggle({ label, hint, checked, onChange, disabled }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">{label}</div>
                <div className="text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">{hint}</div>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                disabled={disabled}
                className={`relative h-6 w-11 flex-none rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                    checked ? 'bg-pdf-cat-solid dark:bg-orange-500' : 'bg-[#ECE8E4] dark:bg-zinc-700'
                }`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        checked ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
            </button>
        </div>
    );
}

export default function CompressPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [quality, setQuality] = useState(DEFAULT_COMPRESS_PDF_SETTINGS.quality);
    const [removeMetadata, setRemoveMetadata] = useState(DEFAULT_COMPRESS_PDF_SETTINGS.removeMetadata);
    const [grayscale, setGrayscale] = useState(DEFAULT_COMPRESS_PDF_SETTINGS.grayscale);
    const [compressStreams, setCompressStreams] = useState(DEFAULT_COMPRESS_PDF_SETTINGS.compressStreams);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);

    // Clear stale results/errors after a page edit without resetting the active tab.
    const clearResultsOnly = useCallback(() => {
        setResults([]);
        setError(null);
        setProgress(null);
    }, []);

    // fileKey -> array of editable pages, or 'oversize' for PDFs with too many pages to edit.
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(files, clearResultsOnly);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const pdfs = list.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
        if (!pdfs.length) {
            const msg = t('compressPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of pdfs) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('compressPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
        setResults([]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResults([]);
        setError(null);
    };

    const handleReset = () => {
        setFiles([]);
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
        resetPageEdits();
    };

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const handleDownloadZip = async () => {
        if (!results.length) return;
        const zip = await zipPdfFiles(results.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'compressed-pdfs.zip');
    };

    const handleCompress = async () => {
        if (!files.length || processing) return;

        // Guard: a file whose every page was removed cannot be compressed.
        for (const file of files) {
            const pages = pagesByKey[fileKeyOf(file)];
            if (Array.isArray(pages) && pages.length && pages.every((p) => p.removed)) {
                const msg = t('compressPdf.errors.removeAll');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                return;
            }
        }

        recordToolUsage('pdf/compress', { category: 'pdf' });

        setProcessing(true);
        setResults([]);
        setError(null);
        const output = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                setProgress({ fileIndex: i + 1, fileTotal: files.length, page: 0, pageTotal: 0 });

                // Apply page edits (rotate / crop / delete) before compressing, if any.
                const pages = pagesByKey[fileKeyOf(file)];
                const source = Array.isArray(pages) && pagesHaveEdits(pages)
                    ? await applyPdfPageEdits(file, pages)
                    : file;

                const blob = await compressPdfFile(
                    source,
                    { quality, removeMetadata, grayscale, compressStreams },
                    (page, pageTotal) => setProgress({ fileIndex: i + 1, fileTotal: files.length, page, pageTotal }),
                );

                const base = file.name.replace(/\.pdf$/i, '');
                output.push({
                    name: `${base}-compressed.pdf`,
                    originalSize: file.size,
                    compressedSize: blob.size,
                    blob,
                });
            }
            setResults(output);
            notifications.show({ title: t('common.success'), message: t('compressPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('compressPdf.errors.failed');
            if (err?.message === 'password') msg = t('compressPdf.errors.password');
            else if (err?.message === 'removeAll') msg = t('compressPdf.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: files.length > 0,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`compressPdf.howTo.step${i}`)),
        [t],
    );

    const activeResult = results[previewIndex] ?? results[0] ?? null;

    const successLabel = results.length > 1
        ? `${t('compressPdf.resultsTitle')} (${toBnDigits(results.length)})`
        : t('compressPdf.done');

    const resultDownloads = useMemo(() => {
        if (!results.length) return undefined;
        return results.map((r) => {
            const savedBytes = Math.max(0, r.originalSize - r.compressedSize);
            const savedPercent = r.originalSize > 0
                ? (savedBytes / r.originalSize) * 100
                : 0;
            return {
                id: r.name,
                name: r.name,
                originalSize: formatFileSize(r.originalSize),
                compressedSize: formatFileSize(r.compressedSize),
                reductionPercent: savedPercent.toFixed(1),
                onDownload: () => downloadBlob(r.blob, r.name),
                onPrint: () => printBlob(r.blob),
            };
        });
    }, [results]);

    const buildProgressLabel = () => {
        if (!progress) return t('compressPdf.compressing');

        return progress.fileTotal > 1
            ? t('compressPdf.progressFileOnly', {
                file: toBnDigits(progress.fileIndex),
                files: toBnDigits(progress.fileTotal),
            })
            : t('compressPdf.compressing');
    };

    const processingLabel = buildProgressLabel();

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('compressPdf.done'),
    });

    return (
        <ToolPageLayout
            slug="pdf/compress"
            subtitleKey="compressPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || results.length > 0 || error ? handleReset : undefined}
            primaryAction={{
                label: t('compressPdf.compress'),
                onClick: handleCompress,
                disabled: !files.length || processing || results.length > 0,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('compressPdf.dropTitle')}
                            hint={t('compressPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                            {files.map((file, index) => (
                                <PdfFileRow
                                    key={`${file.name}-${file.size}`}
                                    name={file.name}
                                    meta={formatFileSize(file.size)}
                                    selected={previewIndex === index}
                                    onSelect={() => setPreviewIndex(index)}
                                    onRemove={() => removeFile(index)}
                                    removeLabel={t('compressPdf.removeFile')}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
            settings={(
                // Every change drops finished results — they no longer match the settings.
                <ToolCard title={t('compressPdf.settingsTitle')}>
                    <div className="flex flex-col gap-4">
                        <label className="block">
                            <div className="mb-1.5 flex items-center justify-between">
                                <span className="text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                    {t('compressPdf.qualityLabel')}
                                </span>
                                <span className="text-[13px] font-semibold tabular-nums text-[#6B635C] dark:text-zinc-400">
                                    {toBnDigits(quality)}%
                                </span>
                            </div>
                            <input
                                type="range"
                                min={10}
                                max={100}
                                step={1}
                                value={quality}
                                disabled={processing}
                                onChange={(e) => { setQuality(Number(e.target.value)); invalidateResults(); }}
                                className="w-full accent-pdf-cat-solid disabled:opacity-45"
                            />
                            <div className="mt-1 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('compressPdf.qualityHint')}
                            </div>
                        </label>

                        <SettingToggle
                            label={t('compressPdf.removeMetadata')}
                            hint={t('compressPdf.removeMetadataHint')}
                            checked={removeMetadata}
                            disabled={processing}
                            onChange={(next) => { setRemoveMetadata(next); invalidateResults(); }}
                        />
                        <SettingToggle
                            label={t('compressPdf.grayscale')}
                            hint={t('compressPdf.grayscaleHint')}
                            checked={grayscale}
                            disabled={processing}
                            onChange={(next) => { setGrayscale(next); invalidateResults(); }}
                        />
                        <SettingToggle
                            label={t('compressPdf.compressStreams')}
                            hint={t('compressPdf.compressStreamsHint')}
                            checked={compressStreams}
                            disabled={processing}
                            onChange={(next) => { setCompressStreams(next); invalidateResults(); }}
                        />
                    </div>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('compressPdf.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('compressPdf.previewEmpty')}
                    idleHint={t('compressPdf.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.fileTotal
                        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
                        : undefined}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('compressPdf.download')}
                    printLabel={t('compressPdf.print')}
                    downloads={resultDownloads}
                    downloadVariant="compress"
                    bundleDownload={results.length > 1
                        ? { label: t('compressPdf.downloadZip'), onClick: handleDownloadZip }
                        : undefined}
                    onEditAgain={results.length > 0 ? clearResultsOnly : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={results.length > 0 ? handleReset : undefined}
                    againLabel={t('compressPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('compressPdf.compress')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={(files.length > 0 || activeResult) && !processing && !error}
                >
                    {!processing && !error && results.length > 0 ? (
                        <PdfDocPreview
                            files={files}
                            resultBlobs={results.map((r) => r.blob)}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : !processing && !error && files.length > 0 ? (
                        <PdfPageEditor
                            files={files}
                            pagesByKey={pagesByKey}
                            onPagesChangeForKey={updatePagesForKey}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
